import {
    Button, Col, Row, Spin, message, Form, Input,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import CardDataBase from '../../../CardDataBase';
import { EWorkspaceCountry } from '../../../../types/workspaces';
import { getPaymentInfoFromCountry } from '../utils/workspace';
import { getWorkspacePaymentInfo, updateWorkspacePaymentInfo } from '../../../../api/workspace-payment-info';
import InputEditable from '../../../InputEditable';

interface IBankDetail {
    workspaceID: string;
    ownerID: string;
    country: EWorkspaceCountry;
}

const BankDetail = ({
    workspaceID,
    ownerID,
    country,
} : IBankDetail) => {
    const [form] = Form.useForm();
    const [isLoading, setIsLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const paymentInfo = form.getFieldsValue();

    const CARD_CONTENT = useMemo(() => {
        const content = [
            {
                key: 'bank',
                title: 'Bank',
                content: paymentInfo?.bank,
            },
            {
                key: 'accountNumber',
                title: 'Account Number',
                content: paymentInfo?.accountNumber,
            },
            {
                key: 'accountHolder',
                title: 'Account Holder',
                content: paymentInfo?.accountHolder,
            },
            {
                key: 'holderIdNumber',
                title: 'Holder ID Number',
                content: paymentInfo?.holderIdNumber,
            },
            {
                key: 'countryCode',
                title: 'Country code',
                content: paymentInfo?.countryCode,
            },
            {
                key: 'phone',
                title: 'Phone',
                content: paymentInfo?.phone,
            },
            {
                key: 'email',
                title: 'Email',
                content: paymentInfo?.email,
            },
        ];

        const paymentInfoFromCountry = getPaymentInfoFromCountry(country, paymentInfo);

        return [...content, ...paymentInfoFromCountry];
    }, [paymentInfo, country]);

    const fetchPaymentInfo = async () => {
        try {
            setIsLoading(true);
            const res = await getWorkspacePaymentInfo(workspaceID);
            form.setFieldsValue(res.data.data);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setIsSaving(true);

            const payload = form.getFieldsValue();

            await form.validateFields();

            const res = await updateWorkspacePaymentInfo({
                ...payload,
                workspaceID,
                country,
                registeredBy: ownerID,
            });
            form.setFieldsValue(res.data.data);
            message.success('Payment info updated successfully');
        } catch (error) {
            console.error(error);
            message.error('Error updating payment info');
        } finally {
            setIsSaving(false);
            setIsEditing(false);
        }
    };

    useEffect(() => {
        fetchPaymentInfo();
    }, [workspaceID]);

    return (
        <CardDataBase
            title="Bank details"
            style={{ minHeight: 345 }}
            innerTitleContentStyle={{ justifyContent: 'space-between', width: '100%' }}
            action={[
                <Row>
                    {isEditing ? (
                        <Row style={{ gap: 16 }}>
                            <Button onClick={() => setIsEditing(false)}>Cancel</Button>
                            <Button onClick={handleSave} loading={isSaving} type="primary">Save</Button>
                        </Row>
                    ) : (
                        <Button onClick={() => setIsEditing(true)}>Edit</Button>
                    )}
                </Row>,
            ]}
        >
            {isLoading ? <Spin style={{ width: '100%', height: '100%', marginTop: 50 }} /> : (
                <Form form={form} style={{ marginTop: 24 }}>
                    {CARD_CONTENT.map((data) => (
                        <Col span={24} key={data.key} style={{ padding: 0 }}>
                            <Form.Item name={data.key} rules={[{ required: true, message: 'Required' }]}>
                                <InputEditable
                                    inputComponent={Input}
                                    label={data.title}
                                    placeholder={data.title}
                                    type="text"
                                    editing={isEditing}
                                    labelType={false}
                                    rowStyle={{ margin: 0, padding: 0 }}
                                    colStyle={{ margin: 0, padding: 0 }}
                                />
                            </Form.Item>
                        </Col>
                    ))}
                </Form>
            )}
        </CardDataBase>
    );
};

export default BankDetail;
